import { observer } from 'mobx-react-lite'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import TextFieldPassword from 'reusable/mui/text-field-password/TextFieldPassword'
import onEnterProp from 'reusable/utils/on-enter-prop'
import useStore from 'reusable/mobx-store-utils'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import AuthCardWrapper from 'auth/shared/AuthCardWrapper'
import { useSignUpAdvisorStore } from '../../SignUpAdvisorStore'
import SignUpAdvisorCreateAccountStore, { CreateAccountState, SignUpAdvisorCreateAccountStoreProvider, useSignUpAdvisorCreateAccountStore } from './SignUpAdvisorCreateAccountStore'
import SignUpWithXero from '../xero/SignUpWithXero'
import AuthSignupTerms from 'auth/sign-up/AuthSignupTerms'
import { useAuthStore } from 'auth/AuthStore'
import CircularProgress from '@mui/material/CircularProgress'
import { themeConfig } from '@gap/shared/theme'
import AuthVerification from 'auth/verification/AuthVerification'
import useInitialMount from 'reusable/hooks/useInitialMount'
import Box from '@mui/material/Box'
import CardGiftcardOutlined from '@mui/icons-material/CardGiftcardOutlined'
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined'
import SecurityOutlined from '@mui/icons-material/SecurityOutlined'
import TextFieldEmail from 'reusable/mui/text-field-email/TextFieldEmail'
import { useEffect } from 'react'

const trialBenefits = [
   { icon: <CardGiftcardOutlined />, title: '14 day free trial', description: 'Full access to all features throughout the trial.' },
   { icon: <CreditCardOutlined />, title: 'No credit card', description: 'No payment details required.' },
   { icon: <SecurityOutlined />, title: '100% secure', description: 'Your data is secure and never shared or used for AI training.' },
]

export default observer(function SignUpAdvisorCreateAccount() {
   const signUpAdvisorStore = useSignUpAdvisorStore()
   const regionRouterStore = useRegionRouterStore()
   const authStore = useAuthStore()
   const signUpAdvisorCreateAccountStore = useStore(sp => new SignUpAdvisorCreateAccountStore(sp), { regionRouterStore, authStore, signUpAdvisorStore })

   useEffect(() => {
      if (!authStore.cognitoAttributes?.userId && authStore.isXeroUser) {
         signUpAdvisorCreateAccountStore.onAccountVerification()
      }
   }, [signUpAdvisorCreateAccountStore, authStore])

   useInitialMount(() => {
      if (regionRouterStore.pathWithoutRegion === '/trial/verification') {
         // We startoff on the verification page, navigate to the login page.
         // User should have an unverified account. On login we navigate to the verification anyway and verify their account
         regionRouterStore.navigate('/')
         authStore.pendingTrialRedirect = true
      }
   })

   if (signUpAdvisorCreateAccountStore.createAccountState === CreateAccountState.verifying) {
      return <AuthVerification
         method="email"
         loginFormFields={signUpAdvisorCreateAccountStore.fields}
         onVerify={signUpAdvisorCreateAccountStore.onAccountVerification}
      />
   }

   if (signUpAdvisorCreateAccountStore.loader.isLoading) {
      return <>
         <Typography variant='body1'>
            Creating your account...
         </Typography>
         <CircularProgress size="32px" />
      </>
   }

   return <SignUpAdvisorCreateAccountStoreProvider store={signUpAdvisorCreateAccountStore}>
      <AuthCardWrapper title={signUpAdvisorCreateAccountStore.title} subtitle={signUpAdvisorCreateAccountStore.subTitle} maxWidth="745px">
         <Stack sx={{ gap: 3 }}>
            {!signUpAdvisorStore.isInvitation && <Stack direction="row" sx={{ gap: 2 }}>
               {trialBenefits.map(benefit => (
                  <Stack key={benefit.title} sx={{ flex: 1, gap: 1 }}>
                     <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: themeConfig.accents.violet[200],
                        color: themeConfig.accents.violet[700],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        'svg': { fontSize: 22 },
                     }}>
                        {benefit.icon}
                     </Box>
                     <Typography variant="body2Bold" sx={{ color: themeConfig.primary[900] }}>
                        {benefit.title}
                     </Typography>
                     <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>
                        {benefit.description}
                     </Typography>
                  </Stack>
               ))}
            </Stack>}
            <form>
               <Stack sx={{ gap: 2, maxWidth: themeConfig.breakpoints.values.md }}>
                  <SignUpWithXero />
                  <Divider>
                     <Typography variant='overline'>or</Typography>
                  </Divider>
                  {signUpAdvisorCreateAccountStore.isEmailSignupExpanded
                     ? <SignUpWithEmail />
                     : <ContinueWithEmail />}
                  <Stack sx={{ pt: 4 }}>
                     <AuthSignupTerms isRegisterAction={false} />
                  </Stack>
               </Stack>
               <Divider sx={{ mt: 3, mb: 1 }} />

               <Box display="flex" alignItems="center" sx={{ mb: -1 }}>
                  <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                     Already have an account?
                  </Typography>
                  <ButtonWithProgress
                     variant="text"
                     onClick={signUpAdvisorCreateAccountStore.navigateToLogin}
                     size="small"
                     sx={{ ml: 2 }}
                  >
                     Log in here
                  </ButtonWithProgress>
               </Box>
            </form>
         </Stack>
      </AuthCardWrapper>
   </SignUpAdvisorCreateAccountStoreProvider>
})

const ContinueWithEmail = observer(function ContinueWithEmail() {
   const signUpAdvisorCreateAccountStore = useSignUpAdvisorCreateAccountStore()
   return <Button
      variant="contained"
      onClick={signUpAdvisorCreateAccountStore.expandEmailSignup}>Continue with email
   </Button>
})

const SignUpWithEmail = observer(function SignUpWithEmail() {
   const signUpAdvisorCreateAccountStore = useSignUpAdvisorCreateAccountStore()

   return <>
      <Stack sx={{ gap: 2 }}>
         <Stack direction="row">
            {signUpAdvisorCreateAccountStore.formHandler.firstName.bindInput(ip =>
               <TextField
                  autoFocus
                  fullWidth
                  label="First name*"
                  {...onEnterProp(signUpAdvisorCreateAccountStore.createCognitoAccount)}
                  {...ip}
                  sx={{ mr: 0.5 }}
               />
            )}
            {signUpAdvisorCreateAccountStore.formHandler.lastName.bindInput(ip =>
               <TextField
                  label="Last name*"
                  fullWidth
                  {...onEnterProp(signUpAdvisorCreateAccountStore.createCognitoAccount)}
                  {...ip}
                  sx={{ ml: 0.5 }}
               />
            )}
         </Stack>
         <Stack>
            {signUpAdvisorCreateAccountStore.formHandler.email.bindInput(ip =>
               <TextFieldEmail
                  fullWidth
                  label="Email*"
                  autoComplete='username'
                  {...onEnterProp(signUpAdvisorCreateAccountStore.createCognitoAccount)}
                  ip={ip}
                  disabled={!signUpAdvisorCreateAccountStore.canModifyEmailAddress}
               />
            )}
         </Stack>
         <Stack>
            {signUpAdvisorCreateAccountStore.formHandler.password.bindInput(ip =>
               <TextFieldPassword
                  fullWidth
                  label="Password*"
                  autoComplete='new-password'
                  {...onEnterProp(signUpAdvisorCreateAccountStore.createCognitoAccount)}
                  {...ip}
               />
            )}
         </Stack>
      </Stack>
      <ButtonWithProgress
         loading={signUpAdvisorCreateAccountStore.loader.isLoading}
         variant="contained"
         onClick={signUpAdvisorCreateAccountStore.createCognitoAccount}>
         Sign up
      </ButtonWithProgress>
   </>
})
